import { HttpError } from 'react-admin';
import type { DataProvider, GetListParams, GetOneParams, UpdateParams } from 'react-admin';

import { requirePartner } from '@src/providers/dataProvider/shared';

import { fetchRepairPricing } from './fetchRepairPricing';
import type { RepairPriceCriteria } from './fetchRepairPricing';
import { updateRepairPriceCriteria } from './updateRepairPriceCriteria';
import type { RepairPriceCriteriaUpdateInput } from './updateRepairPriceCriteria';

export interface RepairPriceCriteriaRow extends RepairPriceCriteria {
  id: number;
}

interface RepairPricingDataProvider {
  getList: (
    resource: string,
    params: GetListParams,
  ) => Promise<{ data: RepairPriceCriteriaRow[]; total: number }>;
  getOne: (
    resource: string,
    params: GetOneParams,
  ) => Promise<{ data: RepairPriceCriteriaRow }>;
  getMany: () => Promise<never>;
  getManyReference: () => Promise<never>;
  create: () => Promise<never>;
  update: (
    resource: string,
    params: UpdateParams<RepairPriceCriteriaRow>,
  ) => Promise<{ data: RepairPriceCriteriaRow }>;
  updateMany: () => Promise<never>;
  delete: () => Promise<never>;
  deleteMany: () => Promise<never>;
}

const toCriteriaRow = (criteria: RepairPriceCriteria): RepairPriceCriteriaRow => ({
  ...criteria,
  id: criteria.id,
});

const repairPricingDataProviderImpl: RepairPricingDataProvider = {
  getList: async (_resource, _params) => {
    try {
      const partner = requirePartner();
      const result = await fetchRepairPricing(partner.id);
      if (!result) return { data: [], total: 0 };
      const data = result.criteria.map(toCriteriaRow);
      return { data, total: data.length };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('repairPricingDataProvider.getList failed:', {
        error: error instanceof Error ? error.message : error,
      });
      throw new HttpError('Failed to load repair pricing.', (error as { status?: number }).status || 500);
    }
  },

  getOne: async (_resource, params) => {
    try {
      const partner = requirePartner();
      const result = await fetchRepairPricing(partner.id);
      const criteria = result?.criteria.find((c) => c.id === Number(params.id));
      if (!criteria) {
        throw new HttpError(`Repair pricing criteria ${params.id} not found`, 404);
      }
      return { data: toCriteriaRow(criteria) };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('repairPricingDataProvider.getOne failed:', {
        error: error instanceof Error ? error.message : error,
      });
      throw new HttpError('Failed to load repair pricing criteria.', (error as { status?: number }).status || 500);
    }
  },

  update: async (_resource, params) => {
    try {
      const partner = requirePartner();
      const criteriaId = Number(params.id);
      const { price, rubricCode, vendorCountryCode, tags } = params.data;

      const updates: RepairPriceCriteriaUpdateInput = {
        price,
        rubricCode: rubricCode || null,
        vendorCountryCode: vendorCountryCode || null,
        tags,
      };

      const updated = await updateRepairPriceCriteria(partner.id, criteriaId, updates);
      return { data: toCriteriaRow(updated) };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('repairPricingDataProvider.update failed:', {
        error: error instanceof Error ? error.message : error,
      });
      throw new HttpError('Failed to update repair pricing criteria.', (error as { status?: number }).status || 500);
    }
  },

  getMany: async () => { throw new Error('getMany not implemented for repairPricing resource'); },
  getManyReference: async () => { throw new Error('getManyReference not implemented for repairPricing resource'); },
  create: async () => { throw new Error('create not implemented for repairPricing resource'); },
  updateMany: async () => { throw new Error('updateMany not implemented for repairPricing resource'); },
  delete: async () => { throw new Error('delete not implemented for repairPricing resource'); },
  deleteMany: async () => { throw new Error('deleteMany not implemented for repairPricing resource'); },
};

export const repairPricingDataProvider = repairPricingDataProviderImpl as unknown as DataProvider;
export default repairPricingDataProvider;
